import { useMemo, useState } from 'react';
import { useQuery } from '../hooks/useQuery';
import { useUrlState } from '../hooks/useUrlState';
import {
  api, Role, TierRow, TierYearSummary, User, YearOverrideEntry, YearRoleEntry,
} from '../lib/api';
import {
  SectionLabel, Card, CardHeader, Table, Th, Tr, Td, Badge,
  ActionButton, Spinner, EmptyState, FilterBar, Select,
} from '../components/ui';

const BONUS_ROLES: Role[] = ['IC', 'DM', 'SA'];

function fmtMoney(n: number): string {
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function fmtPct(n: number): string {
  return `${(n * 100).toFixed(2)}%`;
}

const inputStyle: React.CSSProperties = {
  fontSize: 11,
  padding: '3px 6px',
  borderRadius: 'var(--radius-sm)',
  border: '0.5px solid var(--color-border-strong)',
  background: 'var(--color-surface)',
  color: 'var(--color-text)',
};

function currentYear(): number {
  return new Date().getFullYear();
}

// ─── Inline tier editor ──────────────────────────────────────────────────────
// Year is fixed; effective_from is always Jan 1 of that year. Pre-populated
// from `initial` so "Set 2026 tiers" can carry forward last year's set.

function YearTierEditor({
  initial, onSave, onCancel, saving, emptyMeansDelete,
}: {
  initial: TierRow[];
  onSave: (tiers: TierRow[]) => void | Promise<void>;
  onCancel: () => void;
  saving: boolean;
  /** When true, the editor allows zero rows; saving an empty set is treated
   *  as a deletion by the parent. Defaults to false (must keep at least 1). */
  emptyMeansDelete?: boolean;
}) {
  const [rows, setRows] = useState<TierRow[]>(
    initial.length ? initial.map((t) => ({ ...t })) : [{ tier: 1, threshold_amount: 0, multiplier: 0 }],
  );

  function update(i: number, patch: Partial<TierRow>) {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function add() {
    const next = rows.length ? Math.max(...rows.map((r) => r.tier)) + 1 : 1;
    setRows((prev) => [...prev, { tier: next, threshold_amount: 0, multiplier: 0 }]);
  }
  function remove(i: number) {
    setRows((prev) => prev.filter((_, idx) => idx !== i));
  }

  const allRowsValid = rows.every(
    (r) => r.tier >= 1 && r.threshold_amount >= 0 && r.multiplier >= 0,
  );
  // When emptyMeansDelete: zero rows is valid (will trigger a delete).
  // Otherwise must have at least one row.
  const valid = emptyMeansDelete ? allRowsValid : rows.length > 0 && allRowsValid;
  const isEmpty = rows.length === 0;

  const labelStyle: React.CSSProperties = {
    fontSize: 10,
    fontWeight: 500,
    color: 'var(--color-text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '.04em',
    marginBottom: 2,
  };
  const fieldStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', minWidth: 0 };

  return (
    <div style={{ padding: '12px 16px', background: 'var(--color-blue-bg)' }}>
      {isEmpty && (
        <div style={{ padding: '12px 0', fontSize: 12, color: 'var(--color-blue-text)' }}>
          No tiers — saving will remove this {emptyMeansDelete ? 'override' : 'version'} entirely
          and fall back to the {emptyMeansDelete ? 'role default for this year' : 'previous version (if any)'}.
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map((r, i) => (
          <div
            key={i}
            style={{
              display: 'grid',
              gridTemplateColumns: '64px minmax(110px, 1fr) minmax(90px, 1fr) auto auto',
              gap: 8,
              alignItems: 'end',
              minWidth: 0,
            }}
          >
            <div style={fieldStyle}>
              {i === 0 && <span style={labelStyle}>Tier</span>}
              <input
                type="number" min={1} value={r.tier}
                onChange={(e) => update(i, { tier: parseInt(e.target.value || '0') })}
                style={inputStyle}
              />
            </div>
            <div style={fieldStyle}>
              {i === 0 && <span style={labelStyle}>Threshold ($)</span>}
              <input
                type="number" min={0} step={500} value={r.threshold_amount}
                onChange={(e) => update(i, { threshold_amount: parseFloat(e.target.value || '0') })}
                style={inputStyle}
              />
            </div>
            <div style={fieldStyle}>
              {i === 0 && <span style={labelStyle}>Multiplier</span>}
              <input
                type="number" min={0} step={0.001} value={r.multiplier}
                onChange={(e) => update(i, { multiplier: parseFloat(e.target.value || '0') })}
                style={inputStyle}
              />
            </div>
            <span
              title="Preview"
              style={{ fontSize: 11, color: 'var(--color-text-muted)', whiteSpace: 'nowrap', padding: '0 4px' }}
            >
              = {fmtPct(r.multiplier)}
            </span>
            <ActionButton
              onClick={() => remove(i)}
              variant="default"
              disabled={!emptyMeansDelete && rows.length === 1}
            >
              ×
            </ActionButton>
          </div>
        ))}
      </div>
      <div
        style={{
          display: 'flex',
          gap: 8,
          marginTop: 12,
          paddingTop: 10,
          borderTop: '0.5px solid var(--color-border-strong)',
          alignItems: 'center',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <ActionButton onClick={add} variant="default">+ Add tier</ActionButton>
        <div style={{ display: 'flex', gap: 8 }}>
          <ActionButton onClick={onCancel} variant="default">Cancel</ActionButton>
          <ActionButton
            onClick={() => valid && onSave(rows)}
            variant={isEmpty ? 'danger' : 'primary'}
            disabled={!valid || saving}
          >
            {saving ? 'Saving…' : isEmpty ? 'Delete' : 'Save'}
          </ActionButton>
        </div>
      </div>
    </div>
  );
}

// ─── Tier table (read-only render) ───────────────────────────────────────────

function TierTable({ tiers }: { tiers: TierRow[] }) {
  if (tiers.length === 0) {
    return (
      <div style={{ padding: '14px 16px', fontSize: 12, color: 'var(--color-text-muted)' }}>
        No tiers configured.
      </div>
    );
  }
  return (
    <Table>
      <thead>
        <tr>
          <Th>Tier</Th>
          <Th right>Threshold</Th>
          <Th right>Multiplier</Th>
        </tr>
      </thead>
      <tbody>
        {tiers.map((t) => (
          <Tr key={t.tier}>
            <Td>Tier {t.tier}</Td>
            <Td right>{fmtMoney(t.threshold_amount)}</Td>
            <Td right>{fmtPct(t.multiplier)}</Td>
          </Tr>
        ))}
      </tbody>
    </Table>
  );
}

// ─── Per-role section ────────────────────────────────────────────────────────

function RoleYearCard({
  year, entry, onChanged,
}: {
  year: number;
  entry: YearRoleEntry;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  async function save(rows: TierRow[]) {
    setSaving(true);
    try {
      await api.admin.tierConfig.save(entry.role, `${year}-01-01`, rows);
      setEditing(false);
      onChanged();
    } catch (e) {
      alert(`Save failed: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  async function clearYearVersion() {
    if (!confirm(`Delete the ${year} tier set for ${entry.role}? It will fall back to whatever earlier version is in effect.`)) return;
    try {
      await api.admin.tierConfig.delete(entry.role, `${year}-01-01`);
      onChanged();
    } catch (e) {
      alert(`Delete failed: ${(e as Error).message}`);
    }
  }

  const hasOwnYearVersion = !entry.is_inherited && entry.effective_from?.startsWith(`${year}-`);
  const inheritedFromYear = entry.is_inherited ? entry.effective_from?.slice(0, 4) : null;

  return (
    <Card style={{ marginBottom: 14 }}>
      <CardHeader>
        <span style={{ fontSize: 12, fontWeight: 500 }}>{entry.role}</span>
        <span style={{ marginLeft: 8 }}>
          {entry.effective_from ? (
            entry.is_inherited
              ? <Badge variant="amber">Inherited from {inheritedFromYear}</Badge>
              : <Badge variant="green">Set for {year}</Badge>
          ) : (
            <Badge variant="red">Not configured</Badge>
          )}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {!editing && (
            <>
              <ActionButton onClick={() => setEditing(true)} variant="primary">
                {hasOwnYearVersion ? `Edit ${year}` : `Set ${year} tiers`}
              </ActionButton>
              {hasOwnYearVersion && (
                <ActionButton onClick={clearYearVersion} variant="danger">Delete {year}</ActionButton>
              )}
            </>
          )}
        </div>
      </CardHeader>

      {editing ? (
        <YearTierEditor
          initial={entry.tiers}
          onSave={save}
          onCancel={() => setEditing(false)}
          saving={saving}
        />
      ) : (
        <TierTable tiers={entry.tiers} />
      )}
    </Card>
  );
}

// ─── Per-user override section ───────────────────────────────────────────────

function UserOverridesCard({
  year, summary, users, onChanged,
}: {
  year: number;
  summary: TierYearSummary;
  users: User[];
  onChanged: () => void;
}) {
  const [urlUserId, setUrlUserId] = useUrlState('user', '');
  const selectedUserId = urlUserId || users[0]?.jira_user_id || '';
  const setSelectedUserId = setUrlUserId;
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Find this user's override active for the year. There may be multiple in the
  // year (mid-year change); the one effective at year-start (or earliest in year)
  // is the "yearly" one.
  const activeForYear: YearOverrideEntry | undefined = useMemo(() => {
    const userOverrides = summary.overrides.filter((o) => o.user.jira_user_id === selectedUserId);
    if (userOverrides.length === 0) return undefined;
    // Prefer one effective Jan 1 of selected year, else carry-forward, else first in-year.
    const yearStart = `${year}-01-01`;
    return (
      userOverrides.find((o) => o.effective_from === yearStart)
      ?? userOverrides.find((o) => !o.is_in_year)
      ?? userOverrides[0]
    );
  }, [summary.overrides, selectedUserId, year]);

  const selectedUser = users.find((u) => u.jira_user_id === selectedUserId);
  const roleDefault = summary.roles.find((r) => r.role === selectedUser?.role);

  // Pre-populate the editor: existing override → its tiers; else role default.
  const editorInitial: TierRow[] = activeForYear?.tiers ?? roleDefault?.tiers ?? [{ tier: 1, threshold_amount: 0, multiplier: 0 }];

  async function save(rows: TierRow[]) {
    setSaving(true);
    try {
      // Empty rows: caller wants to remove the override entirely. We delete
      // the version active for this year (if any) — saving an empty set
      // would otherwise hit the API's "tiers must be a non-empty array" guard.
      if (rows.length === 0) {
        if (activeForYear) {
          await api.admin.tierOverrides.delete(selectedUserId, activeForYear.effective_from);
        }
      } else {
        await api.admin.tierOverrides.save(selectedUserId, `${year}-01-01`, rows);
      }
      setEditing(false);
      onChanged();
    } catch (e) {
      alert(`Save failed: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  async function clearYearOverride() {
    if (!activeForYear) return;
    if (!confirm(`Delete ${selectedUser?.display_name}'s override (effective ${activeForYear.effective_from})? They will fall back to the ${selectedUser?.role} role default.`)) return;
    try {
      await api.admin.tierOverrides.delete(selectedUserId, activeForYear.effective_from);
      onChanged();
    } catch (e) {
      alert(`Delete failed: ${(e as Error).message}`);
    }
  }

  // Overrides are year-bound: anything returned for this year was set this
  // year. The button label distinguishes "set on Jan 1" from a mid-year
  // change; both are deletable.
  const hasYearOverride = !!activeForYear && activeForYear.effective_from === `${year}-01-01`;
  const canDeleteActive = !!activeForYear;

  return (
    <Card>
      <CardHeader>
        <span style={{ fontSize: 12, fontWeight: 500 }}>Per-user override</span>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>
          A full replacement of the role default for the chosen user.
        </span>
      </CardHeader>
      <FilterBar>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>User</span>
        <Select value={selectedUserId} onChange={(v) => { setSelectedUserId(v); setEditing(false); }}>
          {[...users]
            .sort((a, b) => {
              if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
              return a.display_name.localeCompare(b.display_name);
            })
            .map((u) => (
              <option key={u.jira_user_id} value={u.jira_user_id}>
                {u.display_name} ({u.role}{u.title ? ` · ${u.title}` : ''}){u.is_active ? '' : ' (inactive)'}
              </option>
            ))}
        </Select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {!editing && (
            <>
              <ActionButton onClick={() => setEditing(true)} variant="primary" disabled={!selectedUserId}>
                {hasYearOverride ? `Edit ${year} override` : `Add override for ${year}`}
              </ActionButton>
              {canDeleteActive && (
                <ActionButton onClick={clearYearOverride} variant="danger">
                  {hasYearOverride
                    ? `Delete ${year} override`
                    : `Delete override (effective ${activeForYear!.effective_from})`}
                </ActionButton>
              )}
            </>
          )}
        </div>
      </FilterBar>

      <div style={{ padding: '8px 16px', background: 'var(--color-border)', display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
        {activeForYear ? (
          <Badge variant="amber">Override set for {year}</Badge>
        ) : (
          <>
            <Badge variant="gray">No override</Badge>
            <span style={{ color: 'var(--color-text-muted)' }}>
              {selectedUser
                ? `Using ${selectedUser.role} role default for ${year}.`
                : `Using role default for ${year}.`}
            </span>
          </>
        )}
      </div>

      {editing ? (
        <YearTierEditor
          initial={editorInitial}
          emptyMeansDelete
          onSave={save}
          onCancel={() => setEditing(false)}
          saving={saving}
        />
      ) : activeForYear ? (
        <TierTable tiers={activeForYear.tiers} />
      ) : (
        <div style={{ padding: '20px 16px', fontSize: 12, color: 'var(--color-text-muted)', textAlign: 'center' }}>
          No override for {year}. Click <strong>Add override for {year}</strong> above to define one.
        </div>
      )}
    </Card>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function AdminTiersPage() {
  const [yearStr, setYearStr] = useUrlState('year', String(currentYear()));
  const year = parseInt(yearStr) || currentYear();
  const setYear = (y: number) => setYearStr(String(y));

  const summaryQuery = useQuery(() => api.admin.tierYearSummary(year), [year]);
  const usersQuery = useQuery(() => api.admin.users.list());

  const summary = summaryQuery.data;
  const users = usersQuery.data?.users ?? [];

  const yearOptions = useMemo(() => {
    const cy = currentYear();
    const years: number[] = [];
    for (let y = cy - 2; y <= cy + 2; y++) years.push(y);
    return years;
  }, []);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <SectionLabel>Bonus tiers</SectionLabel>
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Year</span>
          <Select value={String(year)} onChange={(v) => setYear(parseInt(v))}>
            {yearOptions.map((y) => <option key={y} value={String(y)}>{y}</option>)}
          </Select>
        </span>
      </div>

      <SectionLabel>Role defaults — {year}</SectionLabel>
      {summaryQuery.error ? (
        <EmptyState message={summaryQuery.error} />
      ) : summaryQuery.isLoading || !summary ? (
        <Spinner />
      ) : (
        BONUS_ROLES.map((role) => {
          const entry = summary.roles.find((r) => r.role === role)
            ?? { role, effective_from: null, is_inherited: false, tiers: [] };
          return <RoleYearCard key={role} year={year} entry={entry} onChanged={summaryQuery.refetch} />;
        })
      )}

      <SectionLabel>Per-user overrides — {year}</SectionLabel>
      {usersQuery.error ? (
        <EmptyState message={usersQuery.error} />
      ) : summaryQuery.error ? (
        <EmptyState message={summaryQuery.error} />
      ) : usersQuery.isLoading || !summary ? (
        <Spinner />
      ) : users.length === 0 ? (
        <EmptyState message="No users." />
      ) : (
        <UserOverridesCard
          year={year}
          summary={summary}
          users={users}
          onChanged={summaryQuery.refetch}
        />
      )}
    </>
  );
}
